/**
 * Support request state — Task 13.1, requirements 9.3, 10.1, and 15.1–15.5.
 *
 * Built on the shared `useRequest`/`useApiMutation` in `api/request-state.ts` rather than on a second state
 * machine, so 401, 403, a field-error body, and a contract failure are classified the same way here as on
 * every content screen.
 *
 * ## Why the detail hook owns the mutations
 *
 * A status change returns the new status and the event it recorded — not a whole request — so the screen has
 * to apply that to what it already has. Doing it inside the hook keeps two rules in one place: apply the
 * returned state immediately, then reconcile in the background against a fresh read. 13.3 needs both, and a
 * component that did its own patching would be the second place the shape of a transition is understood.
 *
 * No page reload after a mutation, ever. That is the requirement, and it is also the difference between a
 * queue someone can work through and one that loses their filters every time they claim something.
 */
import type {
  SupportAssignmentChange,
  SupportNoteInput,
  SupportRequestDetail,
  SupportRequestList,
  SupportRequestListQuery,
  SupportRetentionHoldInput,
  SupportStatusChange,
} from '@apuestapr/contracts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ApiClient, ApiFailure, ApiResult } from '../api/client.ts';
import {
  accessFor,
  useRequest,
  type MutationState,
  type RequestState,
} from '../api/request-state.ts';
import { createSupportAdminApi } from './api.ts';

export const useSupportQueue = (
  client: ApiClient,
  query: SupportRequestListQuery | undefined,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): RequestState<SupportRequestList> => {
  const api = useMemo(() => createSupportAdminApi(client), [client]);
  // Serialised, so a caller passing a fresh object literal each render does not reload forever.
  const key = JSON.stringify(query ?? null);
  const load = useCallback(
    () => api.list((JSON.parse(key) as SupportRequestListQuery | null) ?? undefined),
    [api, key],
  );
  return useRequest(load, onFailure);
};

export interface ManagedSupportRequest {
  readonly state: RequestState<SupportRequestDetail>;
  readonly mutation: MutationState<SupportRequestDetail>;
  readonly reconciling: boolean;
  readonly changeStatus: (input: SupportStatusChange) => Promise<ApiResult<unknown>>;
  readonly changeAssignment: (input: SupportAssignmentChange) => Promise<ApiResult<unknown>>;
  readonly addNote: (input: SupportNoteInput) => Promise<ApiResult<unknown>>;
  readonly changeRetentionHold: (input: SupportRetentionHoldInput) => Promise<ApiResult<unknown>>;
  readonly reconcile: () => Promise<void>;
}

/**
 * One support request, with its mutations.
 *
 * Each mutation applies the fields the API returned to the detail already on screen, then reconciles. The
 * partial application matters: a status transition returns `status`, `resolvedAt`, and one event, so
 * reopening has to clear `resolvedAt` and append the event rather than wait for the reconciliation to
 * arrive — otherwise the screen shows a resolved timestamp on a request that is open again.
 */
export const useManagedSupportRequest = (
  client: ApiClient,
  publicId: string,
  csrfToken: string,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): ManagedSupportRequest => {
  const api = useMemo(() => createSupportAdminApi(client), [client]);
  const [state, setState] = useState<RequestState<SupportRequestDetail>>({ status: 'loading' });
  const [mutation, setMutation] = useState<MutationState<SupportRequestDetail>>({
    status: 'ready',
  });
  const [reconciling, setReconciling] = useState(false);
  const mounted = useRef(true);
  const generation = useRef(0);
  const activeId = useRef(publicId);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reconcile = useCallback(async (): Promise<void> => {
    const current = generation.current + 1;
    generation.current = current;
    setReconciling(true);
    const result = await api.detail(publicId);
    if (!mounted.current || activeId.current !== publicId || current !== generation.current) return;
    setReconciling(false);
    if (result.ok) setState({ status: 'ready', data: result.data });
    else onFailure?.(result.failure);
  }, [api, onFailure, publicId]);

  useEffect(() => {
    let active = true;
    activeId.current = publicId;
    generation.current += 1;
    queueMicrotask(() => {
      if (active) {
        setState({ status: 'loading' });
        setMutation({ status: 'ready' });
        setReconciling(false);
      }
    });
    void api.detail(publicId).then((result) => {
      if (!active) return;
      if (result.ok) setState({ status: 'ready', data: result.data });
      else {
        onFailure?.(result.failure);
        setState({ status: 'error', failure: result.failure, access: accessFor(result.failure) });
      }
    });
    return () => {
      active = false;
    };
  }, [api, onFailure, publicId]);

  const runMutation = useCallback(
    async <Value>(
      operation: () => Promise<ApiResult<Value>>,
      apply: (detail: SupportRequestDetail, value: Value) => SupportRequestDetail,
    ): Promise<ApiResult<Value>> => {
      setMutation({ status: 'loading' });
      const result = await operation();
      if (!mounted.current || activeId.current !== publicId) return result;

      if (!result.ok) {
        onFailure?.(result.failure);
        setMutation({
          status: 'error',
          failure: result.failure,
          access: accessFor(result.failure),
        });
        return result;
      }

      let applied: SupportRequestDetail | undefined;
      setState((current) => {
        if (current.status !== 'ready') return current;
        applied = apply(current.data, result.data);
        return { status: 'ready', data: applied };
      });
      setMutation({ status: 'ready', ...(applied === undefined ? {} : { data: applied }) });
      void reconcile();
      return result;
    },
    [onFailure, publicId, reconcile],
  );

  const changeStatus = useCallback(
    (input: SupportStatusChange) =>
      runMutation(
        () => api.changeStatus(publicId, input, csrfToken),
        (detail, transition) => ({
          ...detail,
          status: transition.status,
          resolvedAt: transition.resolvedAt,
          events: transition.event === null ? detail.events : [...detail.events, transition.event],
        }),
      ),
    [api, csrfToken, publicId, runMutation],
  );

  const changeAssignment = useCallback(
    (input: SupportAssignmentChange) =>
      runMutation(
        () => api.changeAssignment(publicId, input, csrfToken),
        (detail, transition) => ({
          ...detail,
          assignedTo: transition.assignedTo,
          events: transition.event === null ? detail.events : [...detail.events, transition.event],
        }),
      ),
    [api, csrfToken, publicId, runMutation],
  );

  const addNote = useCallback(
    (input: SupportNoteInput) =>
      runMutation(
        () => api.addNote(publicId, input, csrfToken),
        // Append-only, and appended at the end: the API returns notes oldest first.
        (detail, note) => ({ ...detail, notes: [...detail.notes, note] }),
      ),
    [api, csrfToken, publicId, runMutation],
  );

  const changeRetentionHold = useCallback(
    (input: SupportRetentionHoldInput) =>
      runMutation(
        () => api.changeRetentionHold(publicId, input, csrfToken),
        (detail, transition) => ({
          ...detail,
          retentionHold: transition.retentionHold,
          events: transition.event === null ? detail.events : [...detail.events, transition.event],
        }),
      ),
    [api, csrfToken, publicId, runMutation],
  );

  return {
    state,
    mutation,
    reconciling,
    changeStatus,
    changeAssignment,
    addNote,
    changeRetentionHold,
    reconcile,
  };
};
